/**
 * `cancelled` is the fifth, settled on CAE-1541 against the design handoff's
 * four: a cancelled Contest is not a closed one.
 */
export const CONTEST_LIFECYCLE_STATES = [
    'draft',
    'scheduled',
    'live',
    'closed',
    'cancelled',
];
/**
 * One Contest status to one lifecycle state.
 *
 * The switch is exhaustive over `ContestStatus` with no `default`, so a ninth
 * status is a compile error here rather than a silent fallback — which is the
 * point of the module.
 *
 * `paused` answers `live` on its own because that is the only sensible guess
 * without a `pausedFromStatus`. A caller holding a whole Contest should use
 * `contestLifecycleState`, which reads it.
 */
export const toContestLifecycleState = (status) => {
    switch (status) {
        case 'draft':
            return 'draft';
        case 'upcoming':
            return 'scheduled';
        case 'open':
        case 'in_play':
        case 'paused':
            return 'live';
        case 'settling':
        case 'complete':
            return 'closed';
        case 'cancelled':
            return 'cancelled';
    }
};
/**
 * The status a Contest is really in, seeing through a pause.
 *
 * Exported because `closed` is two statuses that read differently — `settling`
 * is still working, `complete` has finished — and a caller that cares about only
 * one of them has to be able to tell them apart.
 *
 * `pausedFromStatus` is typed as the full `ContestStatus`, so it can say
 * `paused` — which nothing writes, but a row that did would send a naive unwrap
 * round in circles. Falls back to `open`, the status a paused Contest is
 * assumed to have come from and the only one worth guessing.
 */
export const underlyingContestStatus = (contest) => {
    if (contest.status !== 'paused')
        return contest.status;
    const pausedFrom = contest.pausedFromStatus;
    return pausedFrom && pausedFrom !== 'paused' ? pausedFrom : 'open';
};
/**
 * Where a Contest sits in its lifecycle, with the pause unwrapped.
 *
 * Pause is an overlay, not a state: a Contest paused before it opened is still
 * `scheduled`, and this is what says so. Whether it *is* paused is
 * `contest.status === 'paused'`, which callers that draw a Paused tag read for
 * themselves.
 */
export const contestLifecycleState = (contest) => toContestLifecycleState(underlyingContestStatus(contest));
//# sourceMappingURL=contest-lifecycle.js.map